import { useMemo, type CSSProperties } from 'react';
import type { WidgetLayout } from '@/config/types';
import { parseStyleString } from '@/lib/parseStyleString';

/**
 * The base className every widget root carries.
 *
 * Taken from legacy `componentLayout()` in `utils/functions.ts`. Note that the legacy
 * widgets did **not** all agree with it: several of them hand-rolled
 * `flex w-full flex-col lg:flex-row`, dropping `component-column flex-wrap`, which meant
 * those widgets got no `gap` at `lg` and no wrapping. `componentLayout()` is treated as
 * canonical here — it is the version this ticket was specified against — and a widget that
 * genuinely needs the narrower set overrides it through its own props in SYM-495+.
 *
 * `component-column` is not a Tailwind utility; it is the gutter rule in `global.css`,
 * scoped to `.signage-container`.
 */
export const WIDGET_LAYOUT_BASE_CLASS =
  'flex component-column flex-wrap w-full flex-col lg:flex-row';

/**
 * The base className the widgets that *disagreed* with `componentLayout()` hand-rolled —
 * `componentLayout()` minus `component-column` and `flex-wrap`, and therefore minus the
 * `1.5rem` gutter at `lg`.
 *
 * `DoughnutWidget` is the one this exists for, and it is not a style preference: its two
 * halves are both `lg:w-6/12`, so with the gutter they total `100% + 1.5rem`, and with
 * `flex-wrap` the chart drops onto a second line underneath its own title. Measured in Chrome
 * at 1920px against the `corio` config: two 867px columns in a 1734.67px row with a 33px gap,
 * wrapping at `y` 218 and 339. The legacy app renders them side by side.
 *
 * **Re-measured at the two viewports deployed hardware actually has** (SYM-506's portrait
 * re-run — the numbers above are landscape, which no display is). The exception holds at
 * both, and for the same reason:
 *
 * | viewport | shipped | with the canonical class |
 * | --- | --- | --- |
 * | 1080×1920 | halves at `x` 29 and 540, 512px each, widget 285px tall | 28.5px gap, chart wraps to `y` 293, widget **390px** tall |
 * | 2160×1920 | halves at `x` 33 and 1080, 1047px each, widget 330px tall | 33px gap, chart wraps to `y` 339, widget **451px** tall |
 *
 * The overflow is a percentage, not a pixel count, so it does not go away at a narrower
 * viewport — it is `100% + gap` at every width where `lg:flex-row` applies.
 *
 * So the canonical class stays canonical (MIGRATION decision #18) and this is the documented
 * exception, used by exactly one widget. A widget reaching for it should have a measurement
 * like the one above, not a preference.
 */
export const NARROW_WIDGET_LAYOUT_BASE_CLASS = 'flex w-full flex-col lg:flex-row';

export type WidgetLayoutResult = {
  className: string;
  style: CSSProperties;
};

export type WidgetLayoutOptions = {
  /** Defaults to `WIDGET_LAYOUT_BASE_CLASS`. See `NARROW_WIDGET_LAYOUT_BASE_CLASS`. */
  baseClass?: string;
};

/**
 * `options.layout` -> a className and a style object, for the root element of a widget.
 *
 * Replaces legacy `componentLayout(options)`, which was additionally copy-pasted into
 * almost every widget. Every widget from SYM-491 onwards uses this hook and nothing else.
 *
 * ```tsx
 * const { className, style } = useWidgetLayout(options.layout);
 * return <div className={className} style={style}>…</div>;
 * ```
 *
 * Behaviour preserved verbatim from the legacy function:
 *
 * - `rounded` is appended to the className (`rounded-xl` in every deployed config —
 *   safelisted in `index.css`, since Tailwind never scans the JSON it comes from).
 * - `style`, then `backgroundColor`, then `padding` are concatenated into one declaration
 *   string. **The order is load-bearing**: later declarations overwrite earlier ones, so a
 *   `backgroundColor` beats a `background` inside `style`.
 * - `backgroundColor` maps to the `background` shorthand, not `background-color` — configs
 *   pass gradients through that key.
 * - Falsy values are skipped, including `padding: 0`. Fifteen deployed widgets carry
 *   `"padding": 0` and none of them renders a `padding: 0rem` declaration today.
 */
export function useWidgetLayout(
  layout: WidgetLayout | undefined,
  { baseClass = WIDGET_LAYOUT_BASE_CLASS }: WidgetLayoutOptions = {},
): WidgetLayoutResult {
  const { rounded, style, backgroundColor, padding } = layout ?? {};

  // Memoised on the individual fields rather than on `layout`: the parsed config is
  // immutable so its `layout` object is referentially stable, but a caller that builds one
  // inline would otherwise get a fresh style object on every render and defeat the
  // memoisation of everything below it.
  return useMemo(() => {
    let className = baseClass;
    if (rounded) className += ' ' + rounded;

    let declarations = '';
    if (style) declarations += terminate(style);
    if (backgroundColor) declarations += `background: ${backgroundColor};`;
    if (padding) declarations += `padding: ${String(padding)}rem;`;

    return { className, style: parseStyleString(declarations) };
  }, [baseClass, rounded, style, backgroundColor, padding]);
}

/**
 * Appends the `;` that separates a config-supplied declaration string from the ones this
 * hook appends after it.
 *
 * The legacy code concatenated blindly, so a config whose `style` lacked a trailing `;`
 * would fuse its last declaration with the generated `background:` one and lose both. Every
 * deployed value ends with `;` already, so this changes nothing on any live display — it
 * only stops a hand-edit from blanking a widget. Recorded as MIGRATION decision #17.
 */
function terminate(declarations: string): string {
  return declarations.trimEnd().endsWith(';') ? declarations : declarations + ';';
}
